import { useEffect, useRef } from 'react';
import { trendyolMpApi } from '../../../config/apiClient';

const POLL_INTERVAL_MS = 4000;
const SIGNALR_GRACE_MS = 3500;

export interface TrendyolWebhookEvent {
  timestampUtc?: string;
  eventType?: string;
  payload?: {
    companyId?: string;
    supplierId?: string;
    shipmentPackageId?: number;
    orderNumber?: string;
    status?: string;
    upsertResult?: { success?: boolean };
    success?: boolean;
    [key: string]: unknown;
  };
}

interface UseTrendyolWebhookMonitorOptions {
  companyId: string | null | undefined;
  enabled?: boolean;
  lastSignalRAtRef: React.MutableRefObject<number>;
  onWebhookDetected: (event: TrendyolWebhookEvent) => void;
}

function eventKey(event: TrendyolWebhookEvent): string {
  const p = event.payload ?? {};
  return [
    event.timestampUtc ?? '',
    event.eventType ?? '',
    p.shipmentPackageId ?? '',
    p.orderNumber ?? '',
    p.status ?? '',
  ].join('|');
}

function matchesCompany(event: TrendyolWebhookEvent, companyId: string): boolean {
  const payloadCompanyId = event.payload?.companyId;
  return !payloadCompanyId || String(payloadCompanyId) === String(companyId);
}

/**
 * Trendyol MP API webhook logunu poll eder. Trendyol → ngrok → backend'e düşen
 * olayları SignalR'dan bağımsız yakalar; Aquasoft listesini otomatik yeniler.
 */
export function useTrendyolWebhookMonitor({
  companyId,
  enabled = true,
  lastSignalRAtRef,
  onWebhookDetected,
}: UseTrendyolWebhookMonitorOptions): void {
  const seenKeysRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);
  const lastWebhookAtRef = useRef(0);
  const onDetectedRef = useRef(onWebhookDetected);

  onDetectedRef.current = onWebhookDetected;

  useEffect(() => {
    if (!enabled || !companyId) return;

    let cancelled = false;

    console.info(
      '%c[TY Webhook] Izleme basladi — Trendyol canli webhook burada loglanir',
      'color:#f57c00;font-weight:bold;font-size:13px',
      {
        companyId,
        pollMs: POLL_INTERVAL_MS,
        kaynak: 'GET /api/TrendyolWebhook/recent',
        ipucu: 'Test siparisi olusturun; WEBHOOK_RECEIVED gorunmezse ngrok/Trendyol aboneligi kontrol edin',
      },
    );

    const poll = async () => {
      if (cancelled) return;

      try {
        const response = await trendyolMpApi.get('/api/TrendyolWebhook/recent', { params: { take: 80 } });
        const events: TrendyolWebhookEvent[] = response.data?.events ?? [];

        if (!initializedRef.current) {
          events.forEach((ev) => seenKeysRef.current.add(eventKey(ev)));
          initializedRef.current = true;
          const lastProcessed = events.find((ev) => ev.eventType === 'WEBHOOK_PROCESSED');
          if (lastProcessed) {
            console.info('%c[TY Webhook] Son islenen olay (baslangic)', 'color:#ef6c00', lastProcessed);
          } else {
            console.warn(
              '%c[TY Webhook] Henuz webhook olayi yok — Trendyol aboneligi/ngrok kontrol edin',
              'color:#e65100;font-weight:bold',
              { companyId, ngrokIpucu: 'trendyolUI → Canli Webhook panelinden kayit yapin' },
            );
          }
          return;
        }

        const fresh = events.filter((ev) => {
          const key = eventKey(ev);
          if (seenKeysRef.current.has(key)) return false;
          seenKeysRef.current.add(key);
          return true;
        });

        for (const event of fresh) {
          if (!matchesCompany(event, companyId)) continue;

          const type = event.eventType ?? '';
          const payload = event.payload ?? {};

          if (type === 'WEBHOOK_RECEIVED') {
            lastWebhookAtRef.current = Date.now();
            console.info(
              '%c[TY Webhook] CANLI webhook alindi (Trendyol → ngrok → backend)',
              'color:#2e7d32;font-weight:bold;font-size:13px',
              {
                zaman: event.timestampUtc,
                siparisNo: payload.orderNumber,
                paketId: payload.shipmentPackageId,
                durum: payload.status,
                companyId: payload.companyId ?? companyId,
              },
            );
          }

          if (type === 'WEBHOOK_PROCESSED' && payload.success !== false) {
            lastWebhookAtRef.current = Date.now();
            console.info(
              '%c[TY Webhook] Siparis islendi — Aquasoft listesi yenilenecek',
              'color:#f57c00;font-weight:bold',
              {
                siparisNo: payload.orderNumber,
                paketId: payload.shipmentPackageId,
                durum: payload.status,
              },
            );
            onDetectedRef.current(event);

            window.setTimeout(() => {
              const signalDelta = Date.now() - lastSignalRAtRef.current;
              if (signalDelta > SIGNALR_GRACE_MS) {
                console.warn(
                  '%c[TY Webhook] SignalR bildirimi gelmedi — Main API (5075) calisiyor mu? Polling ile liste yine de yenilendi.',
                  'color:#c62828;font-weight:bold',
                  { signalDeltaMs: signalDelta, mainApi: 'http://localhost:5075' },
                );
              }
            }, SIGNALR_GRACE_MS);
          }

          if (type === 'WEBHOOK_ERROR') {
            console.error('%c[TY Webhook] Backend isleme hatasi', 'color:#c62828;font-weight:bold', event);
          }

          if (type === 'STATUS_CHANGED') {
            console.info('%c[TY Webhook] Durum degisti', 'color:#ef6c00;font-weight:bold', payload);
          }
        }
      } catch (err) {
        console.warn(
          '%c[TY Webhook] Izleme poll hatasi — trendyol MP API (5018) erisilebilir mi?',
          'color:#e65100',
          err,
        );
      }
    };

    void poll();
    const timer = window.setInterval(() => void poll(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      initializedRef.current = false;
      seenKeysRef.current.clear();
      console.info('%c[TY Webhook] Izleme durduruldu', 'color:#9e9e9e');
    };
  }, [companyId, enabled, lastSignalRAtRef]);
}
